import { Button } from '../../shared/components/Button'
import Hero from '../components/Hero'
import Card from '../components/Card'
import { FormFieldRenderer } from '../../util/formFieldRenderer'
import { API_URL, contactFields as fields } from '../../constants'
import { useEffect, useState } from 'react'
import DialogModal from '../../shared/components/DialogModal'
import { validateFields } from '../../util/validateField'
import { handleFormChange } from '../../util/handleFormChange'
import { useTranslation } from 'react-i18next'
import { useTranslationService } from '../../util/TranslationContext'
import { languageUtil } from '../../util/languageUtils'
import { fetchWithLanguage } from '../../util/fetchFunction'
import { ErrorOverlay, LoadingOverlay } from '../../shared/components/Loading'

const AboutUs = () => {
    const [form, setForm] = useState({})
    const [isOpen, setIsOpen] = useState(false)
    const [errors, setErrors] = useState({})
    const [footer, setFooter] = useState({})
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const { language } = languageUtil()

    const handleData = handleFormChange(setForm)

    const { t } = useTranslation()

    const { translateFields } = useTranslationService()
    const contactFields = translateFields(fields, 'contact')

    const programTypes = [
        {
            label: t('aboutUs.programTypes.remoteVolunteering.label'),
            imageSrc:
                'https://images.unsplash.com/photo-1701656827553-01a790f63363?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NTR8fHZvbHVudGVlcnxlbnwwfHwwfHx8MA%3D%3D',
            description: t('aboutUs.programTypes.remoteVolunteering.description'),
        },
        {
            label: t('aboutUs.programTypes.communityOutreach.label'),
            imageSrc:
                'https://images.unsplash.com/photo-1522543558187-768b6df7c25c?q=80&w=1170&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
            description: t('aboutUs.programTypes.communityOutreach.description'),
        },
        {
            label: t('aboutUs.programTypes.educationMentorship.label'),
            imageSrc:
                'https://plus.unsplash.com/premium_photo-1681842143575-03bf1be4c11c?q=80&w=1186&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
            description: t('aboutUs.programTypes.educationMentorship.description'),
        },
    ]

    const submitContact = async () => {
        const errors = validateFields(contactFields, form, setErrors, t)
        if (Object.keys(errors).length > 0) {
            setErrors(errors)
            return
        }

        try {
            const response = await fetch(`${API_URL}/contact-us`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(form),
            })

            if (!response.ok) {
                const errorData = await response.json()
                console.error('Submission failed:', errorData)
                setError(`Submission failed: ${error}`)
                // Optional: display server-side errors
                return
            }

            const result = await response.json()
            setIsOpen(true)
        } catch (error) {
            console.error('An error occurred:', error)
            setError(`An error occurred: ${error}`)
        }
    }

    useEffect(() => {
        const fetchContact = async () => {
            await fetchWithLanguage('platform-information', setFooter, setError, setLoading, {
                transform: item => ({
                    email: item.email,
                    phone: item.phone_number,
                    address: item.physical_address,
                }),
            })
        }

        fetchContact()
    }, [language])

    return (
        <div>
            {/* Hero */}
            <Hero
                title={t('aboutUs.hero.title')}
                imageUrl='/img/banners/about_us.jpeg'
                size='large'
            >
                <p className='px-4 mb-8 text-base text-center text-white sm:text-lg'>
                    {t('aboutUs.hero.description')}
                </p>
            </Hero>

            <div className='px-4 mx-auto my-8 max-w-7xl'>
                {loading && <LoadingOverlay />}
                {error && <ErrorOverlay message={error} onClose={() => setError(null)} />}
                {/* About Us */}
                <section className='pt-12'>
                    <h2 className='mb-6 text-xl font-bold text-gray-800 sm:text-2xl text-start'>
                        {t('aboutUs.aboutUs.title')}
                    </h2>
                    <div className='flex flex-col gap-8 md:flex-row'>
                        {/* Images */}
                        <div className='flex flex-col w-full gap-4 md:w-1/2'>
                            <div className='flex flex-col h-auto gap-4 sm:flex-row sm:h-36'>
                                <img
                                    src='/img/banners/about_us3.jpeg'
                                    alt='Top Left'
                                    className='object-cover w-full rounded-lg sm:w-1/2 h-36'
                                />
                                <img
                                    src='/img/banners/about_us.jpeg'
                                    alt='Top Right'
                                    className='object-cover w-full rounded-lg sm:w-1/2 h-36'
                                />
                            </div>
                            <img
                                src='/img/banners/about_us2.jpeg'
                                alt='Bottom Full'
                                className='object-cover w-full rounded-lg h-36'
                            />
                        </div>

                        {/* Text */}
                        <div className='w-full md:w-1/2'>
                            <p className='text-sm leading-relaxed text-gray-700 sm:text-base'>
                                {t('aboutUs.aboutUs.content.0')}
                                <br />
                                <br />
                                {t('aboutUs.aboutUs.content.1')}
                                <br />
                                <br />
                                {t('aboutUs.aboutUs.content.2')}
                            </p>
                        </div>
                    </div>
                </section>

                {/* <hr className='h-px my-8 bg-gray-300 md:my-16' /> */}

                {/* Impact Cards */}
                {/* <section>
                    <h2 className='mb-6 text-xl font-bold text-gray-800 sm:text-2xl text-start'>
                        {t('aboutUs.impact.title')}
                    </h2>
                    <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3'>
                        {programTypes.map((item, index) => (
                            <Card
                                key={index}
                                imageSrc={item.imageSrc}
                                label={item.label}
                                description={item.description}
                                button={false}
                            />
                        ))}
                    </div>
                </section> */}

                <DialogModal
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    title={t('aboutUs.contact.successModal.title')}
                >
                    <p className='mb-6 text-sm text-gray-600'>
                        {t('aboutUs.contact.successModal.message')}
                    </p>
                </DialogModal>

                <hr className='h-px my-8 bg-gray-300' />

                {/* Contact Information Section */}
                <section className='max-w-4xl p-6 mx-auto mb-8 bg-transparent rounded-xl'>
                    <div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
                        {/* Phone & WhatsApp */}
                        <div className='p-5 text-center border bg-emerald-50 rounded-xl border-emerald-100'>
                            <div className='inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-emerald-100'>
                                <svg
                                    className='w-6 h-6 text-emerald-600'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z'
                                    />
                                </svg>
                            </div>
                            <h3 className='mb-3 text-lg font-semibold text-gray-800'>
                                {t('aboutUs.aboutUs.contactInfo.callUs')}
                            </h3>
                            <div className='space-y-2'>
                                <div>
                                    {/* <p className='text-sm text-gray-600'>
                                        {' '}
                                        {t('aboutUs.aboutUs.contactInfo.phone')}
                                    </p> */}
                                    <a
                                        href='tel:+967777347631'
                                        className='text-lg font-medium text-gray-900 hover:text-emerald-600'
                                    >
                                        {footer.phone}
                                    </a>
                                </div>
                                {/* <div>
                                    <p className='text-sm text-gray-600'>
                                        {' '}
                                        {t('aboutUs.aboutUs.contactInfo.whatsapp')}
                                    </p>
                                    <a
                                        href='https://wa.me/967734433347'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='text-lg font-medium text-gray-900 hover:text-green-600'
                                    >
                                        {footer.phone}
                                    </a>
                                </div> */}
                            </div>
                        </div>

                        {/* Email */}
                        <div className='p-5 text-center border border-blue-100 bg-blue-50 rounded-xl'>
                            <div className='inline-flex items-center justify-center w-12 h-12 mb-4 bg-blue-100 rounded-full'>
                                <svg
                                    className='w-6 h-6 text-blue-600'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
                                    />
                                </svg>
                            </div>
                            <h3 className='mb-3 text-lg font-semibold text-gray-800'>
                                {' '}
                                {t('aboutUs.aboutUs.contactInfo.emailUs')}
                            </h3>
                            {/* <p className='mb-2 text-sm text-gray-600'>
                                {' '}
                                {t('aboutUs.aboutUs.contactInfo.sendUsEmail')}
                            </p> */}
                            <a
                                href='mailto:strive2helpye@gmail.com'
                                className='text-lg font-medium text-gray-900 break-all hover:text-blue-600'
                            >
                                {footer.email}
                            </a>
                        </div>

                        {/* Location */}
                        <div className='p-5 text-center border bg-amber-50 rounded-xl border-amber-100'>
                            <div className='inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-amber-100'>
                                <svg
                                    className='w-6 h-6 text-amber-600'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
                                    />
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'
                                    />
                                </svg>
                            </div>
                            <h3 className='mb-3 text-lg font-semibold text-gray-800'>
                                {' '}
                                {t('aboutUs.aboutUs.contactInfo.visitUs')}
                            </h3>
                            {/* <p className='mb-2 text-sm text-gray-600'>
                                {' '}
                                {t('aboutUs.aboutUs.contactInfo.yemenBranch')}
                            </p> */}
                            <p className='text-gray-900'>
                                {footer.address}
                                {/* {t('aboutUs.aboutUs.contactInfo.location1')}

                                <br />
                                {t('aboutUs.aboutUs.contactInfo.location2')}
                                <br />
                                {t('aboutUs.aboutUs.contactInfo.location3')} */}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Contact Form Section */}
                <section
                    className='max-w-2xl p-4 mx-auto bg-white border shadow-md sm:p-6 rounded-xl'
                    id='form'
                >
                    <h2 className='mb-8 text-xl font-bold text-center text-gray-800 sm:text-2xl'>
                        {t('aboutUs.contact.title') || 'Send us a Message'}
                    </h2>

                    <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                        {contactFields.map(field => (
                            <div
                                key={field.name}
                                className={
                                    field.name === 'message' ? 'col-span-1 sm:col-span-2' : ''
                                }
                            >
                                <FormFieldRenderer
                                    value={form[field.name]}
                                    field={field}
                                    onChange={handleData}
                                    error={errors[field.name]}
                                />
                            </div>
                        ))}
                    </div>
                    <div className='flex justify-end w-full mt-6'>
                        <Button size='large' onClick={submitContact}>
                            {t('aboutUs.contact.submitButton')}
                        </Button>
                    </div>
                </section>

                <hr className='h-px my-8 bg-gray-300 md:my-16' />
            </div>
        </div>
    )
}

export default AboutUs
